import dayjs from 'dayjs';
import { DayRules } from '../../utils/dates';

export type CalendarEvent = {
  start: string;
  end: string;
  type: 'open' | 'break';
  label: string;
};

const parseClockTime = (time: string) => {
  const [hours = '0', minutes = '0', seconds = '0'] = time.split(':');

  return dayjs().hour(Number(hours)).minute(Number(minutes)).second(Number(seconds)).millisecond(0);
};

/**
 * Builds the calendar blocks (events) to render for a single day.
 */
export const buildEvents = (dayData: DayRules[number], t: (key: string) => string): CalendarEvent[] => {
  const events: CalendarEvent[] = [];

  const hasOpenHours = dayData.openStartTime && dayData.openEndTime;

  const openStart = parseClockTime(dayData.openStartTime);
  const openEnd = parseClockTime(dayData.openEndTime);

  const breaks = (dayData.breaks || [])
    .map((b) => ({
      start: parseClockTime(b.startTime),
      end: parseClockTime(b.endTime),
      name: b.name || t('Break'),
    }))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  // Always render ALL breaks
  const breakEvents: CalendarEvent[] = breaks.map((b) => ({
    start: b.start.format('HH:mm:ss'),
    end: b.end.format('HH:mm:ss'),
    type: 'break' as const,
    label: b.name,
  }));

  // If no open hours, only render breaks.
  if (!hasOpenHours) {
    return breakEvents;
  }

  // Only use breaks that overlap WITHIN open hours for splitting
  const relevantBreaks = breaks.filter((b) => b.end.isAfter(openStart) && b.start.isBefore(openEnd));

  let cursor = openStart;

  relevantBreaks.forEach((b) => {
    // Clamp break to open window.
    const breakStart = b.start.isAfter(openStart) ? b.start : openStart;
    const breakEnd = b.end.isBefore(openEnd) ? b.end : openEnd;

    // Open segment before break.
    if (cursor.isBefore(breakStart)) {
      events.push({
        start: cursor.format('HH:mm:ss'),
        end: breakStart.format('HH:mm:ss'),
        type: 'open',
        label: t('Open'),
      });
    }

    cursor = breakEnd;
  });

  // Remaining open segment after last break.
  if (cursor.isBefore(openEnd)) {
    events.push({
      start: cursor.format('HH:mm:ss'),
      end: openEnd.format('HH:mm:ss'),
      type: 'open',
      label: t('Open'),
    });
  }

  // Combine open + ALL breaks (including those outside open hours).
  return [...events, ...breakEvents];
};

export const getPosition = (time: string, startHour: number, pixelsPerHour: number): number => {
  const d = parseClockTime(time);
  const hour = d.hour() + d.minute() / 60;
  return (hour - startHour) * pixelsPerHour;
};

export const formatTime = (time: string): string => {
  return parseClockTime(time).format('h:mm A');
};
